import { formatDateWithJavaFormat } from '@disy/lib.date-format/date-format';
import { assertUnreachable } from '@disy/lib/custom-error';

import type { AttributePrintInfo } from 'cadenza/query-language/attribute-info';
import type { Context } from 'cadenza/query-language/context';
import type { NotQlExpression, QlExpression } from 'cadenza/repository/model/ql-condition';
import { ArithmeticQlExpressionOperator, ComparisonQlExpressionOperator } from 'cadenza/repository/model/ql-condition';

export class Printer {
  readonly #attributeIdsToAttributeInfos: Record<string, AttributePrintInfo>;
  #attributeInScope?: AttributePrintInfo;

  constructor(context: Context) {
    this.#attributeIdsToAttributeInfos = Object.fromEntries(
      Object.entries(context.allAttributes).flatMap(([ot, attrs]) =>
        Object.entries(attrs).map(([attr, { attributeId, format }]) => [
          attributeId,
          { objecttypePrintName: ot, attributePrintName: attr, dateFormat: format },
        ]),
      ),
    );
  }

  print(dto?: QlExpression) {
    if (!dto) {
      return '';
    }
    const result: string[] = [];
    this.#print(dto, result);
    return result.join('');
  }

  #print(dto: QlExpression, result: string[], parent?: QlExpression) {
    // `NotExpression`s are special-cased, as their position differs: IS NOT vs. NOT LIKE vs. NOT IN
    if ('triviaBefore' in dto && !(dto.expressionType === 'notExpression' && shouldPushNotDown(dto))) {
      result.push(triviaBefore(dto));
    }
    const explicitParentheses = !!parent && requiresAdditionalParentheses(dto, parent);
    if (explicitParentheses) {
      result.push('(');
    }

    // In some `binaryExpression`, `inExpression`, and `likeExpression`, we have to print an enveloping NOT as infix.
    const infixNot = parent?.expressionType === 'notExpression' && shouldPushNotDown(parent) ? parent : undefined;

    switch (dto.expressionType) {
      case 'multiArityExpression': {
        let operandsLength = dto.operands.length;
        for (let i = 0; i < operandsLength; i++) {
          const operand = dto.operands[i];
          this.#print(operand, result, dto);
          if (i < operandsLength - 1) {
            if (!effectiveTriviaAfter(operand)) {
              result.push(' ');
            }
            const operator = dto.triviaOperators?.[i] ?? dto.type;
            result.push(operator);
            if (!effectiveTriviaBefore(dto.operands[i + 1])) {
              result.push(' ');
            }
          }
        }
        break;
      }
      case 'notExpression': {
        if (!shouldPushNotDown(dto)) {
          result.push(dto.triviaOperator ?? 'NOT');
          if (!effectiveTriviaBefore(dto.operand)) {
            result.push(' ');
          }
        }
        this.#print(dto.operand, result, dto);
        break;
      }
      case 'binaryExpression': {
        this.#print(dto.lhs, result, dto);
        const operatorSymbol =
          dto.operator === 'EQUALS' ?
            (dto.triviaOperator ?? (dto.rhs.expressionType === 'nullLiteralExpression' ? 'IS' : '='))
          : ComparisonQlExpressionOperator.toSymbol(dto.operator);
        const isTextualOperator = operatorSymbol.charCodeAt(0) > 0x40;
        if (isTextualOperator ? !effectiveTriviaAfter(dto.lhs) : hasNoTriviaAfter(dto.lhs)) {
          result.push(' ');
        }
        result.push(operatorSymbol);
        if (dto.operator === 'EQUALS' && infixNot) {
          // handle `IS NOT`
          result.push(infixNot.triviaBefore ?? ' ');
          result.push(infixNot.triviaOperator ?? 'NOT');
          result.push(triviaAfter(infixNot));
        }
        if (isTextualOperator ? !effectiveTriviaBefore(dto.rhs) : hasNoTriviaBefore(dto.rhs)) {
          result.push(' ');
        }
        this.#print(dto.rhs, result, dto);
        break;
      }
      case 'arithmeticExpression': {
        this.#print(dto.lhs, result, dto);
        if (hasNoTriviaAfter(dto.lhs)) {
          result.push(' ');
        }
        const operator = ArithmeticQlExpressionOperator.toSymbol(dto.operator);
        result.push(operator);
        if (hasNoTriviaBefore(dto.rhs)) {
          result.push(' ');
        }
        this.#print(dto.rhs, result, dto);
        break;
      }
      case 'inExpression': {
        this.#print(dto.lhs, result, dto);
        if (hasNoTriviaAfter(dto.lhs) && !infixNot?.triviaBefore) {
          result.push(' ');
        }
        // handle `NOT IN`
        if (infixNot) {
          result.push(triviaBefore(infixNot));
          result.push(infixNot.triviaOperator ?? 'NOT');
          result.push(infixNot.triviaAfter ?? ' ');
        }
        result.push(dto.triviaOperator ?? 'IN');
        result.push(' (');
        let operandsLength = dto.literals.length;
        for (let i = 0; i < operandsLength; i++) {
          const literal = dto.literals[i];
          if (i > 0 && hasNoTriviaBefore(literal)) {
            result.push(' ');
          }
          this.#print(literal, result, dto);
          if (i < operandsLength - 1) {
            result.push(',');
          }
        }
        result.push(')');

        break;
      }
      case 'likeExpression': {
        this.#print(dto.queryElement, result, dto);
        if (hasNoTriviaAfter(dto.queryElement) && !infixNot?.triviaBefore) {
          result.push(' ');
        }
        // handle `NOT LIKE`
        if (infixNot) {
          result.push(triviaBefore(infixNot));
          result.push(infixNot.triviaOperator ?? 'NOT');
          result.push(infixNot.triviaAfter ?? ' ');
        }
        result.push(dto.triviaOperator ?? 'LIKE');
        if (!effectiveTriviaBefore(dto.searchTerm)) {
          result.push(' ');
        }
        this.#print(dto.searchTerm, result, dto);
        break;
      }
      case 'numberLiteralExpression':
        result.push(dto.value);
        break;
      case 'stringLiteralExpression':
        result.push(`"${dto.value}"`);
        break;
      case 'variableLiteralExpression':
        result.push(`{{${dto.value}}}`);
        break;
      case 'dateTimeLiteralExpression': {
        const dateFormat = this.#attributeInScope?.dateFormat;
        if (!dateFormat) {
          throw new Error(`expected date attribute in scope to format date value, but was ${this.#attributeInScope}`);
        }
        const formattedDate = formatDateWithJavaFormat(dto.value, dateFormat);
        result.push(formattedDate);
        break;
      }
      case 'nullLiteralExpression':
        result.push(dto.triviaLiteral ?? 'NULL');
        break;
      case 'queryElementExpression': {
        this.#attributeInScope = this.#attributeIdsToAttributeInfos[dto.attributeId];
        result.push(
          this.#attributeInScope ?
            `'${this.#attributeInScope.objecttypePrintName}'.'${this.#attributeInScope.attributePrintName}'`
          : "'MISSING'.'ATTRIBUTE'",
        );
        break;
      }
    }

    if (explicitParentheses) {
      result.push(')');
    }
    // `NotExpression`s are special-cased, as their position differs: IS NOT vs. NOT LIKE vs. NOT IN
    if ('triviaAfter' in dto && (dto.expressionType !== 'notExpression' || !shouldPushNotDown(dto))) {
      result.push(triviaAfter(dto));
    }
  }
}

export function shouldPushNotDown(expr: NotQlExpression) {
  const negated = expr.operand;
  return (
    negated.expressionType === 'likeExpression' ||
    negated.expressionType === 'inExpression' ||
    (negated.expressionType === 'binaryExpression' &&
      negated.operator === 'EQUALS' &&
      negated.rhs.expressionType === 'nullLiteralExpression')
  );
}

function requiresAdditionalParentheses(child: QlExpression, parent: QlExpression) {
  if (isEnclosedByParentheses(child)) {
    return false;
  }
  const childPrecedence = getPrecedence(child);
  const parentPrecedence = getPrecedence(parent);
  return (
    childPrecedence > parentPrecedence ||
    (childPrecedence === parentPrecedence && childIsRhsOfNoncommutative(parent, child))
  );
}

function getPrecedence(expr: QlExpression) {
  const expressionType = expr.expressionType;
  switch (expressionType) {
    case 'dateTimeLiteralExpression':
    case 'nullLiteralExpression':
    case 'numberLiteralExpression':
    case 'stringLiteralExpression':
    case 'variableLiteralExpression':
    case 'queryElementExpression':
      return 0;
    case 'arithmeticExpression': {
      const operator = expr.operator;
      switch (operator) {
        case 'MUL':
        case 'DIV':
          return 1;
        case 'ADD':
        case 'SUB':
          return 2;
        default:
          return assertUnreachable(operator);
      }
    }
    case 'binaryExpression':
    case 'inExpression':
    case 'likeExpression':
      return 3;
    case 'notExpression':
      return 4;
    case 'multiArityExpression': {
      const operator = expr.type;
      switch (operator) {
        case 'AND':
          return 5;
        case 'OR':
          return 6;
        default:
          return assertUnreachable(operator);
      }
    }
    default:
      return assertUnreachable(expressionType);
  }
}

function childIsRhsOfNoncommutative(parent: QlExpression, child: QlExpression) {
  return (
    parent.expressionType === 'arithmeticExpression' &&
    (parent.operator === 'SUB' || parent.operator === 'DIV') &&
    child === parent.rhs
  );
}

function isEnclosedByParentheses(expr: QlExpression) {
  return triviaBefore(expr).indexOf('(') > -1 && triviaAfter(expr).indexOf(')') > -1;
}

function hasNoTriviaBefore(expr: QlExpression) {
  return effectiveTriviaBefore(expr) === undefined;
}

function effectiveTriviaBefore(expr: QlExpression): string | undefined {
  if (expr.expressionType === 'notExpression' && shouldPushNotDown(expr)) {
    return effectiveTriviaBefore(expr.operand);
  }
  const triviaBefore = expr.triviaBefore;
  switch (expr.expressionType) {
    case 'multiArityExpression':
      return concatTrivia(triviaBefore, effectiveTriviaBefore(expr.operands[0]));
    case 'binaryExpression':
    case 'arithmeticExpression':
    case 'inExpression':
      return concatTrivia(triviaBefore, effectiveTriviaBefore(expr.lhs));
    case 'likeExpression':
      return concatTrivia(triviaBefore, effectiveTriviaBefore(expr.queryElement));
  }
  return triviaBefore;
}

function hasNoTriviaAfter(expr: QlExpression) {
  return effectiveTriviaAfter(expr) === undefined;
}

function effectiveTriviaAfter(expr: QlExpression): string | undefined {
  if (expr.expressionType === 'notExpression' && shouldPushNotDown(expr)) {
    return effectiveTriviaAfter(expr.operand);
  }
  const triviaAfter = expr.triviaAfter;
  switch (expr.expressionType) {
    case 'inExpression':
      // Both `IN` and functions end with closing parenthesis, so any check for empty-string trivia is unnecessary.
      return triviaAfter === '' ? ')' : triviaAfter;
    case 'multiArityExpression':
      return concatTrivia(effectiveTriviaAfter(expr.operands[expr.operands.length - 1]), triviaAfter);
    case 'binaryExpression':
    case 'arithmeticExpression':
      return concatTrivia(effectiveTriviaAfter(expr.rhs), triviaAfter);
    case 'likeExpression':
      return concatTrivia(effectiveTriviaAfter(expr.searchTerm), triviaAfter);
    case 'notExpression':
      return concatTrivia(effectiveTriviaAfter(expr.operand), triviaAfter);
  }
  return triviaAfter;
}

function concatTrivia(left: string | undefined, right: string | undefined) {
  if (left === undefined) {
    return right;
  }
  if (right === undefined) {
    return left;
  }
  return left + right;
}

function triviaBefore(token: QlExpression) {
  return token.triviaBefore ?? '';
}

function triviaAfter(token: QlExpression) {
  return token.triviaAfter ?? '';
}
